import {NavigateFunction, redirect} from 'react-router-dom';
import {axiosInterceptorsService} from '@techsee/techsee-client-infra/lib/services/AxiosInterceptorsService';
import {getAppTracer} from '../../../app.tracer';
import {RouteName, RouteNameType} from '../../router/route-names';
import {CHECK_PENDING_REQUEST_ATTEMPTS, CHECK_PENDING_REQUEST_INTERVAL} from './ts-react-state-helper.settings';

const trace = getAppTracer('TsReactStateHelper');

/**
 * Maps route name constants to React Router URL paths.
 * Used by navigateTo() to resolve state names to paths.
 */
const STATE_TO_PATH: Record<string, string> = {
    [RouteName.LOGIN]: '/login',
    [RouteName.LOGIN_CALLBACK]: '/login-callback',
    [RouteName.INVITE]: '/invite',
    [RouteName.DASHBOARD]: '/dashboard',
    [RouteName.DASHBOARD_MAIN]: '/dashboard/main',
    [RouteName.DASHBOARD_ENTRY]: '/dashboard/entry',
    [RouteName.DASHBOARD_STORE]: '/dashboard/store',
    [RouteName.HISTORY]: '/history',
    [RouteName.VJ_HISTORY]: '/vjHistory',
    [RouteName.WIFI_ANALYZER_HISTORY]: '/wifiAnalyzerHistory',
    [RouteName.HISTORY_CONSOLE]: '/historyConsole',
    [RouteName.SESSION_ENDED]: '/session-ended',
    [RouteName.INVITATION_ENDED]: '/invitation-ended',
    [RouteName.EMB_DASHBOARD_ERROR]: '/emb-dashboard-error',
    [RouteName.BROWSER_NOT_SUPPORTED]: '/browser-not-supported',
    [RouteName.CHANGE_PASSWORD]: '/changePassword',
    [RouteName.CONNECTIVITY_STANDALONE]: '/connectivity-self-check',
    [RouteName.CONNECTIVITY_CHECK]: '/connectivityCheck',
    [RouteName.CONNECTIVITY_GURU_DATA]: '/connectivity-guru-data',
    [RouteName.JOIN]: '/join',
    [RouteName.GUEST]: '/guest',
    [RouteName.MULTIPARTY_DASHBOARD]: '/multipartyDashboard',
    [RouteName.NWAY]: '/nway',
    [RouteName.REACTIVATE_ACCOUNT]: '/reactivate-account',
    [RouteName.REVOKE_ACCOUNT]: '/revoke-account',
    [RouteName.OAUTH_CALLBACK]: '/oauth/callback'
};

function resolveStatePath(state: string): string {
    if (state.startsWith('/')) {
        return state;
    }

    if (state === '.') {
        return window.location.pathname;
    }

    return STATE_TO_PATH[state] || '/' + state.replace(/\./g, '/');
}

function buildUrlWithParams(path: string, params?: Record<string, any>): string {
    if (!params || Object.keys(params).length === 0) {
        return path;
    }

    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            const serialized = typeof value === 'object' ? JSON.stringify(value) : String(value);
            searchParams.append(key, serialized);
        }
    });

    const queryString = searchParams.toString();

    if (queryString) {
        return path + (path.includes('?') ? '&' : '?') + queryString;
    }

    return path;
}

async function waitForPendingRequests(): Promise<void> {
    let attempts = 0;

    while (axiosInterceptorsService.hasPendingRequests() && attempts < CHECK_PENDING_REQUEST_ATTEMPTS) {
        attempts++;
        await new Promise<void>((resolve) => setTimeout(resolve, CHECK_PENDING_REQUEST_INTERVAL));
    }

    if (axiosInterceptorsService.hasPendingRequests()) {
        trace.warn('waitForPendingRequests: max attempts reached, proceeding with navigation');
    }
}

export interface ITsReactStateHelper {
    navigateTo(
        state: RouteNameType,
        params?: Record<string, any>,
        options?: {replace?: boolean; notify?: boolean}
    ): Promise<void>;
    setNavigate(navigate: NavigateFunction | undefined): void;
    enterLoaderContext(): void;
    exitLoaderContext(): void;
}

/**
 * React navigation service - centralized point for all navigation in React app.
 * In loader context: navigateTo() throws redirect() Response, stopping loader execution.
 * In controller context: navigateTo() calls this._navigate() for imperative navigation.
 * Accessed via AppRootStore: rootStore.tsReactStateHelper
 */
export class TsReactStateHelper implements ITsReactStateHelper {
    private _navigate?: NavigateFunction;
    private _loaderContextDepth = 0;
    private _safeNavigateInProgress = false;

    /**
     * Inject navigate function from React Router (called from RootLayout)
     */
    setNavigate(navigate: NavigateFunction | undefined): void {
        this._navigate = navigate;
    }

    /**
     * Enter loader context. While depth > 0, navigateTo() throws redirect().
     * Called by wrapLoaderWithContext in routing infrastructure.
     */
    enterLoaderContext(): void {
        this._loaderContextDepth++;
    }

    /**
     * Exit loader context. Decrements depth counter (min 0).
     * Called by wrapLoaderWithContext in routing infrastructure.
     */
    exitLoaderContext(): void {
        this._loaderContextDepth = Math.max(0, this._loaderContextDepth - 1);
    }

    /**
     * Unified navigation for both loaders and controllers.
     * In loader context (depth > 0): throws redirect() Response — React Router catches it.
     * In controller context (depth === 0): calls this._navigate() for imperative navigation.
     * Handles Angular-style options like {notify: false} (update URL without navigation).
     * Callers in route loaders must await navigateTo() so pending HTTP requests can drain
     * before redirect; otherwise navigation may be delayed while loader logic continues.
     */
    async navigateTo(
        state: RouteNameType,
        params?: Record<string, any>,
        options?: {replace?: boolean; notify?: boolean}
    ): Promise<void> {
        if (this._safeNavigateInProgress) {
            trace.info('navigateTo: skipped, navigation already in progress');

            return;
        }

        this._safeNavigateInProgress = true;

        try {
            const path = resolveStatePath(state);
            const url = buildUrlWithParams(path, params);

            await waitForPendingRequests();

            // notify: false means update URL without triggering navigation (Angular $state.go behavior)
            if (options?.notify === false) {
                const fullUrl = new URL(url, window.location.origin);

                window.history.replaceState(null, '', fullUrl.pathname + fullUrl.search);
                trace.info('navigateTo: URL updated without navigation', {state, url});

                return;
            }

            trace.info('navigateTo', {state, path, url, loaderContext: this._loaderContextDepth > 0});

            // Loader context: throw redirect to stop execution and let React Router handle it
            if (this._loaderContextDepth > 0) {
                throw redirect(url);
            }

            // Controller context: imperative navigation
            if (this._navigate) {
                this._navigate(url, {replace: options?.replace});
            } else {
                trace.warn('navigate function not set, falling back to window.location');
                window.location.href = url;
            }
        } finally {
            this._safeNavigateInProgress = false;
        }
    }
}
